import QuotaDashboard from '@/contexts/quota-dashboard/domain/quota-dashboard'
import QuotaDTO from '@/contexts/quota-dashboard/infrastructure/quotaDTO'
import { FlatVariantForQuota, mergeVariantAndProduct } from '@/core/mapper/quotaConfigMapper'
import QuotaDashboardDatasource from '@/contexts/quota-dashboard/infrastructure/quota-dashboard.datasource'
import { OfferDTO } from '@/contexts/quota-dashboard/infrastructure/offerDTO'

function getQuotaByMeshId(quotaRawList: QuotaDTO[]): Map<string, QuotaDTO[]> {
  return quotaRawList.reduce((acc, quota) => {
    const quotasForOffer: QuotaDTO[] = acc.get(quota.meshId) || []
    quotasForOffer.push(quota)
    acc.set(quota.meshId, quotasForOffer)
    return acc
  }, new Map<string, QuotaDTO[]>())
}

export default class QuotaDashboardRepository {
  private readonly datasource: QuotaDashboardDatasource

  constructor(quotaService: QuotaDashboardDatasource) {
    this.datasource = quotaService
  }

  async getQuota(distributorId: number): Promise<QuotaDashboard[]> {
    const quotaRawList: QuotaDTO[] = await this.datasource.findQuotas(distributorId)
    const offersMap = await this.getOfferMap(quotaRawList, distributorId)
    const quotaByMeshId = getQuotaByMeshId(quotaRawList)
    const variantIdByMeshId = new Map(
      Array.from(quotaByMeshId.keys()).map((meshId) => [
        meshId,
        offersMap.get(meshId)?.productUId ?? QuotaDashboard.pricerVariantId(meshId),
      ]),
    )
    const variantMap = await this.getVariantMap(Array.from(variantIdByMeshId.values()), distributorId)

    return Array.from(quotaByMeshId.entries()).map(([meshId, quotasWithSameMeshId]) => {
      const variantId = variantIdByMeshId.get(meshId)
      return QuotaDashboard.buildFrom(
        quotasWithSameMeshId,
        variantId ? variantMap.get(variantId) : undefined,
        offersMap.get(meshId),
      )
    })
  }

  private async getOfferMap(quotaRawList: QuotaDTO[], distributorId: number) {
    const offerIdList = Array.from(new Set(quotaRawList.map((quota) => quota!.meshId)))
    const offers = quotaRawList.length ? await this.datasource.findOffers(offerIdList, distributorId) : []
    return new Map(offers.map((offer) => [offer.offerId, offer]))
  }

  private async getVariantMap(
    variantIds: (string | undefined)[],
    distributorId: number,
  ): Promise<Map<string, FlatVariantForQuota>> {
    const variantIdList = Array.from(new Set(variantIds.filter((variantId): variantId is string => !!variantId)))
    const products = variantIdList.length ? await this.datasource.findProducts(variantIdList, distributorId) : []

    return new Map(products.flatMap(mergeVariantAndProduct))
  }
}
